import { useCallback, useEffect, useState } from 'react';

/**
 * Hook compartilhado pra sincronizar o modo do ServicePropertiesPanel entre
 * Inspector (docked) e Chatbot (floating). Persiste em localStorage e propaga
 * mudancas entre componentes irmaos via CustomEvent local ('mexx:service-panel-mode').
 *
 * O storage event nativo do browser SO dispara entre tabs distintas — pra sync
 * dentro da MESMA tab (nosso caso), precisamos disparar CustomEvent manualmente
 * apos gravar no storage.
 */

export type ServicePanelMode = 'docked' | 'floating';

const KEY = 'mexx.chatbot.service-panel.mode';
const EVENT = 'mexx:service-panel-mode';

const readMode = (): ServicePanelMode => {
    try {
        const raw = localStorage.getItem(KEY);
        return raw === 'floating' ? 'floating' : 'docked';
    } catch { return 'docked'; }
};

const writeMode = (mode: ServicePanelMode) => {
    try {
        localStorage.setItem(KEY, mode);
        // Notifica outros componentes na MESMA tab (storage event so cruza tabs).
        window.dispatchEvent(new CustomEvent<ServicePanelMode>(EVENT, { detail: mode }));
    } catch { /* noop */ }
};

export function useServicePanelMode(): [ServicePanelMode, () => void, (m: ServicePanelMode) => void] {
    const [mode, setModeState] = useState<ServicePanelMode>(readMode);

    useEffect(() => {
        const onChange = (e: Event) => {
            const detail = (e as CustomEvent<ServicePanelMode>).detail;
            if (detail === 'docked' || detail === 'floating') setModeState(detail);
        };
        window.addEventListener(EVENT, onChange);
        return () => window.removeEventListener(EVENT, onChange);
    }, []);

    const toggle = useCallback(() => {
        const next: ServicePanelMode = mode === 'docked' ? 'floating' : 'docked';
        writeMode(next);
        setModeState(next);
    }, [mode]);

    const setMode = useCallback((m: ServicePanelMode) => {
        writeMode(m);
        setModeState(m);
    }, []);

    return [mode, toggle, setMode];
}
